import React from 'react';

import { Box, Button, Stack } from '@mui/material';
import { z } from 'zod';

import { Field, Form } from '@hrlogics/react-components/form';

import { normalizeEmail } from '@clearverify/utils';

import { useChangeEmployeeUserEmailByPlatformAdminMutation } from 'src/api';
import { DetailGridItem } from 'src/components/common';
import { TextField } from 'src/components/finalForm';
import { REQUIRED_VALIDATION_MESSAGE } from 'src/constants/validationMessage';
import { useNotification } from 'src/hooks/app';

type EmployeeChangeEmailFormValues = {
  newEmail: string;
};

type EmployeeChangeEmailModalContentProps = {
  userId: string;
  currentEmail: string;
  onUpdated?: () => void;
  onCloseModal: () => void;
  switchClosability: (closability: boolean) => void;
};

export const EmployeeChangeEmailModalContent: React.FC<EmployeeChangeEmailModalContentProps> = ({
  userId,
  currentEmail,
  onUpdated,
  onCloseModal,
  switchClosability,
}) => {
  const { createApolloContext } = useNotification();

  // No `errorMessage`: it would replace the toast title, and the server's text is the useful part.
  const [changeEmail] = useChangeEmployeeUserEmailByPlatformAdminMutation({
    context: createApolloContext({ successMessage: 'Employee email has been changed' }),
  });

  const handleSubmit = React.useCallback(
    async ({ newEmail }: EmployeeChangeEmailFormValues) => {
      switchClosability(false);
      try {
        await changeEmail({ variables: { userId, newEmail: normalizeEmail(newEmail) } });
        onUpdated?.();
        onCloseModal();
      } catch {
        // Errors surface via the notification ErrorLink.
      } finally {
        switchClosability(true);
      }
    },
    [changeEmail, onCloseModal, onUpdated, switchClosability, userId],
  );

  return (
    <Form<EmployeeChangeEmailFormValues>
      onSubmit={handleSubmit}
      validationSchema={employeeChangeEmailValidationSchema}
      subscription={{ submitting: true }}
    >
      {({ submitting }) => (
        <Stack spacing={2}>
          <DetailGridItem topText="Current email" bottomText={currentEmail} />
          <Field
            name="newEmail"
            component={TextField}
            label="New email address"
            type="email"
            disabled={submitting}
            fullWidth
            required
            autoFocus
          />
          <Box display="flex" alignItems="center" justifyContent="center" gap={2} pt={1}>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              onClick={onCloseModal}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              type="submit"
              loading={submitting}
            >
              Change email
            </Button>
          </Box>
        </Stack>
      )}
    </Form>
  );
};

const employeeChangeEmailValidationSchema = z.object({
  newEmail: z.email({ error: 'Enter a valid email address' }).nonempty({
    error: REQUIRED_VALIDATION_MESSAGE,
  }),
}) satisfies z.ZodType<EmployeeChangeEmailFormValues>;
